import { useNavigate } from "react-router-dom";

function Home() {
  const navigate = useNavigate();

  const capabilities = [
    {
      title: "Engage with Ecosystems",
      desc: "Chat with AI versions of oceans, rainforests, glaciers and more.",
      action: "Start Conversing",
      link: "/select",
    },
    {
      title: "Learn Through Stories",
      desc: "Understand ecosystems through narratives and emotional facts.",
      action: "Read Stories",
      link: "/stories",
    },
    {
      title: "Take Action",
      desc: "Access tips and resources to act on climate and conservation.",
      action: "Find Ways to Help",
      link: "/action",
    },
  ];

  return (
    <div className="bg-white min-h-screen font-sans text-gray-800">
      {/* Hero Section */}
      <section
        className="bg-cover bg-center h-[85vh] flex items-center justify-center relative"
        style={{
          backgroundImage: `url('https://tse1.mm.bing.net/th/id/OIP.pWilRlth83XwpEtzIRNNrgHaFj')`,
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="text-center z-10 text-white px-4 max-w-3xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Converse with Nature, Nurture Our Planet
          </h1>
          <p className="mb-6 text-lg">
            TerraTalk is your AI-powered connection to nature's voice — converse with Earth’s ecosystems through storytelling and action.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate("/select")}
              className="bg-violet-600 hover:bg-violet-700 text-white px-6 py-2 rounded shadow-md transition"
            >
              Start Chatting
            </button>
          </div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-16 px-6 text-center">
        <h2 className="text-2xl font-bold mb-4">Explore TerraTalk's Capabilities</h2>
        <p className="text-gray-600 mb-10">Discover how TerraTalk brings the natural world to your fingertips.</p>

        <div className="grid gap-6 md:grid-cols-3 max-w-5xl mx-auto">
          {capabilities.map(({ title, desc, action, link }, idx) => (
            <div
              key={idx}
              className={`rounded-lg border p-6 text-left transition hover:shadow-md ${
                title === "Take Action" ? "border-violet-400" : "border-gray-200"
              }`}
            >
              <h3 className="font-semibold text-lg mb-2">{title}</h3>
              <p className="text-sm text-gray-600 mb-4">{desc}</p>
              <button
                onClick={() => navigate(link)}
                className="text-sm text-violet-600 hover:underline"
              >
                {action}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* How it Works */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 items-center gap-8">
          <div>
            <h2 className="text-2xl font-bold mb-4">How it Works: Your AI Ecosystem Companion</h2>
            <p className="text-gray-700 mb-4">
              TerraTalk uses advanced AI to simulate conversations with Earth's ecosystems. Choose one, ask questions, and receive poetic, factual replies that drive awareness.
            </p>
            <button
              onClick={() => navigate("/chat/rainforest")}
              className="bg-blue-600 text-white px-5 py-2 rounded mt-2"
            >
              Dive into Chat
            </button>
          </div>
          <img
            src="https://th.bing.com/th/id/OIP.M0PfP1wAqygtcHLJcsKGSAHaEK?pid=ImgDet&rs=1"
            alt="AI assistant"
            className="w-full rounded-lg"
          />
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-6">
        <h2 className="text-2xl font-bold text-center mb-10">What Our Users Say</h2>
        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto text-left">
          {[
            {
              name: "Anya Sharma",
              role: "Environmental Advocate",
              text: "TerraTalk has truly transformed how I think about environmental issues. The stories are so moving, and the chatbot feels incredibly intuitive.",
            },
            {
              name: "Ben Carter",
              role: "Student & Nature Enthusiast",
              text: "I never thought learning about glaciers could be so engaging! TerraTalk makes complex topics accessible and deeply personal.",
            },
            {
              name: "Chloe Davis",
              role: "Community Organizer",
              text: "An invaluable tool for anyone passionate about conservation. The 'How to Help' section is full of practical advice.",
            },
          ].map(({ name, role, text }, i) => (
            <div key={i} className="p-6 border rounded-lg shadow-sm bg-white">
              <p className="italic mb-4">“{text}”</p>
              <div className="text-sm font-medium">{name}</div>
              <div className="text-xs text-gray-500">{role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-violet-600 to-pink-500 text-white py-12 px-6 text-center mt-12">
        <h2 className="text-2xl font-bold mb-2">Ready to Connect with Our Planet?</h2>
        <p className="mb-6">Embark on a journey and discover the wonders of Earth’s ecosystems.</p>
        <button
          onClick={() => navigate("/select")}
          className="bg-white text-violet-600 px-6 py-3 rounded font-semibold hover:bg-gray-100"
        >
          Start Your TerraTalk Journey
        </button>
      </section>

      {/* Footer */}
      <footer className="text-center py-6 text-sm text-gray-500 bg-white border-t mt-4">
        <div className="mb-2">Stay Updated on TerraTalk</div>
        <div className="flex justify-center gap-2 mb-4">
          <input
            type="email"
            placeholder="Enter your email"
            className="border px-3 py-2 rounded text-sm"
          />
          <button className="bg-violet-600 text-white px-4 py-2 rounded">
            Subscribe
          </button>
        </div>
        <p>© 2025 TerraTalk. Built for Sonoma Hacks 4.0 🌿</p>
      </footer>
    </div>
  );
}

export default Home;
